import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  TableCell,
  Table,
  TableHead,
  TableRow,
  TableBody,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Source as SourceIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Attachment as AttachmentIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useDispatch } from 'react-redux';
import { getLeadById } from '../../store/slices/leadsSlice';
import { toast } from 'react-toastify';

const LeadDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  useEffect(() => {
    fetchLeadDetail();
  }, [id]);

  const fetchLeadDetail = async () => {
    try {
      setLoading(true);
      const res = await dispatch(getLeadById(id)).unwrap();
      const leadData = res?.data ?? res;
      if (leadData) {
        setLead(leadData);

      } else {
        toast.error('Failed to fetch lead details');
        navigate('/leads');
      }
    } catch (error) {
      toast.error('Error loading lead details');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/leads');
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImageDialog(true);
  };

  const handleDownloadFile = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded successfully');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };


  const getStatusColor = (statusName) => {
    const colorMap = {
      'New': 'primary',
      'Contacted': 'info',
      'Qualified': 'success',
      'Connected': 'warning',
      'Lost': 'error',
      'Follow up': 'secondary',
      'Chatting': 'info',
      'Select': 'default',
      'Meeting': 'warning',
      'Upcoming': 'info'
    };
    return colorMap[statusName] || 'default';
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    return new Date(dateTimeString).toLocaleString();
  };

  if (loading) {
    return (
      <PageLayout sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </PageLayout>
    );
  }

  if (!lead) {
    return (
      <PageLayout sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Typography>Lead not found</Typography>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header with Breadcrumbs */}
          <Box sx={{ mb: 4 }}>
            <Breadcrumbs sx={{ mb: 2 }}>
              <Link
                color="inherit"
                onClick={handleBack}
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                Leads
              </Link>
              <Typography color="text.primary">Lead Details</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ color: 'blue.600', fontWeight: 600, mb: 1 }}>
                  {lead.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={lead.last_status_log?.status?.name}
                    color={getStatusColor(lead.last_status_log?.status?.name)}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Created: {formatDateTime(lead.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                variant="outlined"
              >
                Back to Leads
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Left Column - Lead Information */}
            <Grid item xs={12} md={8}>
              {/* Basic Information Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon />
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Name
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {lead.name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        DBA Name
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {lead.dba_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Email
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {lead.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Phone
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {lead.phone || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {lead.description || 'No description provided'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Status History Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon />
                    Current Status
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current Status
                      </Typography>
                      <Chip
                        label={lead.last_status_log?.status?.name}
                        color={getStatusColor(lead.last_status_log?.status?.name)}
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Status Description
                      </Typography>
                      <Typography variant="body1">
                        {lead.last_status_log?.description || 'No description'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Status Created
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(lead.last_status_log?.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Created By
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {lead.last_status_log?.author?.name?.charAt(0) || 'U'}
                        </Avatar>
                        <Typography variant="body1">
                          {lead.last_status_log?.author?.name || 'Unknown'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Meeting Information Card */}
              {lead.last_status_log?.meeting && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon />
                      Meeting Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon fontSize="small" />
                            Start Time
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(lead.last_status_log.meeting.start_date_time)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon fontSize="small" />
                            End Time
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(lead.last_status_log.meeting.end_date_time)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LanguageIcon fontSize="small" />
                            Time Zone
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {lead.last_status_log.meeting.time_zone}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Meeting Status
                        </Typography>
                        <Chip
                          label={lead.last_status_log.status?.name}
                          color={getStatusColor(lead.last_status_log.status?.name)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Meeting Description
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {lead.last_status_log.meeting.description || 'No meeting description'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Attachments Card */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: 'blue.600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <AttachmentIcon />
                    Attachments ({lead.attachments?.length || 0})
                  </Typography>

                  {lead.attachments && lead.attachments.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {lead.attachments.map((file) => (
                            <TableRow
                              key={file.id}
                              hover={file.type?.startsWith('image/')}
                              sx={{
                                cursor: file.type?.startsWith('image/') ? 'pointer' : 'default',
                              }}
                              onClick={
                                file.type?.startsWith('image/')
                                  ? () => handleImageClick(file.path)
                                  : undefined
                              }
                            >
                              {/* File Name */}
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DescriptionIcon color="action" />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 200,
                                    }}
                                    title={file.title}
                                  >
                                    {file.title}
                                  </Typography>
                                </Box>
                              </TableCell>

                              {/* File Type */}
                              <TableCell>
                                <Chip
                                  label={file.type?.split('/')[1]?.toUpperCase() || 'FILE'}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>

                              {/* Action Buttons */}
                              <TableCell sx={{ textAlign: 'center' }}>
                                <IconButton
                                  size="small"
                                  href={file.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadFile(file.path, file.title);
                                  }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 3 }}
                    >
                      No attachments found
                    </Typography>
                  )}
                </CardContent>
              </Card>

            </Grid>

            {/* Right Column - Side Information */}
            <Grid item xs={12} md={4}>
              {/* Source & Brand Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SourceIcon />
                    Source & Brand
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <SourceIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Source"
                        secondary={lead.source?.name || 'Not specified'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <BusinessIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Brand"
                        secondary={lead.brand?.name || 'Not specified'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              {/* Assignees Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon />
                    Assignee
                  </Typography>
                  {/* {lead?.last_status_log?.assignee && lead?.last_status_log?.assignee.length > 0 ? ( */}
                  <List dense>
                    {lead?.assignees.map((assignee, index) => (
                    <ListItem key={assignee.id} >
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                          {assignee.name?.charAt(0) || 'U'}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={assignee.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {assignee.email}
                            </Typography>
                            {assignee.phone && (
                              <Typography variant="body2" color="text.secondary">
                                {assignee.phone}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    ))} 
                  </List>
                  {/* ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No assignees
                    </Typography>
                  )} */}
                </CardContent>
              </Card>

              {/* Status Assignee Card */}
              {/* {lead.status?.assignee && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon />
                      Status Assignee
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 48, height: 48 }}>
                        {lead.status.assignee.name?.charAt(0) || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {lead.status.assignee.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.status.assignee.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.status.assignee.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )} */}

              {/* DBA Information Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'blue.600', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon />
                    DBA Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {lead.dba ? 'DBA details available' : 'No DBA associated'}
                  </Typography>
                  {lead.dba && (
                    <Button variant="outlined" fullWidth
                      onClick={() => navigate(`/view-merchant?id=${lead?.dba?.id}`)}
                    >
                      View DBA Details
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
      </Container>

      {/* Image Preview Dialog */}
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={selectedImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImageDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => handleDownloadFile(selectedImage, 'image.png')}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default LeadDetail;